import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope } from '@fortawesome/sharp-solid-svg-icons';
import { usePathname, useRouter } from 'next/navigation';
import { FC, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { twMerge } from 'tailwind-merge';

import Button, { ButtonVariant } from '@/components/button';
import { GLOBAL_TOOLTIP } from '@/constants/constants';
import {
  LATEST_CREATED_LEAD_LIST_DETAILS,
  LATEST_CREATED_LEAD_LIST_PREVIEW,
} from '@/constants/localStorage';
import { LEAD_LIST_RESULTS_URL } from '@/constants/urls';
import {
  createLeadList,
  LeadList,
  LeadListPreview,
} from '@/features/search/leadLists/helpers/createLeadList';
import { useEnhancedSearchParams } from '@/hooks/useEnhancedSearchParams';
import { useAuth } from '@/providers/auth-provider';
import { useToastContext } from '@/providers/ToastProvider';
import type { RootState } from '@/redux/store';
import { multiUnion } from '@/utils/multiUnion';
import { saveJsonInLocalStorage } from '@/utils/webStorage';

interface Props {
  className?: string;
}

const CreateLeadListButton: FC<Props> = ({ className = '' }) => {
  const router = useRouter();
  const pathName = usePathname();
  const { showErrorToast } = useToastContext();
  const { userCanPurchaseLeadLists } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const allSelectionFeatures = useSelector(
    (state: RootState) => state.features.allFeatures
  );

  const { setOrReplaceParamWithRerender } = useEnhancedSearchParams();

  const storeLeadListInfoForFutureDisplay = useCallback(
    (leadList: LeadList, leadListPreview: LeadListPreview) => {
      saveJsonInLocalStorage(LATEST_CREATED_LEAD_LIST_DETAILS, leadList);
      saveJsonInLocalStorage(LATEST_CREATED_LEAD_LIST_PREVIEW, leadListPreview);
    },
    []
  );

  const createList = useCallback(async () => {
    const geometry = multiUnion(allSelectionFeatures)?.geometry;
    const { res, err } = await createLeadList({ filter: { geometry } });
    if (err) showErrorToast('Error creating lead list.');
    if (res?.data) {
      storeLeadListInfoForFutureDisplay({ ...res.data }, res.data.preview);
      if (pathName === LEAD_LIST_RESULTS_URL) {
        setOrReplaceParamWithRerender(
          'shouldRerenderLeadListResultsPage',
          'true'
        );
      } else {
        router.push(LEAD_LIST_RESULTS_URL);
      }
    }
  }, [
    allSelectionFeatures,
    pathName,
    router,
    setOrReplaceParamWithRerender,
    showErrorToast,
    storeLeadListInfoForFutureDisplay,
  ]);

  const handleClick = useCallback(
    async (e) => {
      e.stopPropagation();
      setIsLoading(true);
      await createList();
      setIsLoading(false);
    },
    [createList]
  );

  if (!userCanPurchaseLeadLists || !allSelectionFeatures?.length) return null;

  return (
    <Button
      className={twMerge('text-white', className)}
      data-tooltip-content="Create Lead List"
      data-tooltip-delay-show={200}
      data-tooltip-id={GLOBAL_TOOLTIP}
      isLoading={isLoading}
      variant={ButtonVariant.GHOST}
      onClick={handleClick}
    >
      <FontAwesomeIcon className="text-gray-90" icon={faEnvelope} />
    </Button>
  );
};

export default CreateLeadListButton;
